import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Search, Check, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useTelecomAccess } from "@/hooks/useTelecomAccess";
import { SectorNoHabilitadoPanel } from "@/components/SectorNoHabilitadoPanel";
import { Alert, Button, Card, Input, PageContainer, PageHeader } from "@/components/ui";

interface CertificadoVigente {
  certificadoId: string;
  numCertificado: string;
  fechaEmision: string;
  estatus: string;
  solicitudFolio: string;
  titularId: string;
  titularRazonSocial: string;
  productoNombre: string;
  productoModelo: string;
  marca: string;
}

/**
 * Mapeo del parámetro ?s= (servicio destino) al nombre legible y al builder de
 * la URL de la pantalla dedicada del trámite. Espejo del switch de
 * 2_SeleccionDeCertificado.html. En Fase 1 sólo copia-fiel está cableado.
 */
const SERVICIOS: Record<string, { nombre: string; destino: (certId: string) => string }> = {
  "copia-fiel": {
    nombre: "Copia Fiel de un Certificado",
    destino: (certId) => `/telecom/servicios/copia-fiel?cert=${encodeURIComponent(certId)}`,
  },
  "reexpedicion": {
    nombre: "Reexpedición de Certificado (Pruebas Complementarias)",
    destino: (certId) => `/telecom/servicios/reexpedicion?cert=${encodeURIComponent(certId)}`,
  },
  "ampliacion-paises": {
    nombre: "Ampliación / Reducción de Países",
    destino: (certId) => `/telecom/servicios/ampliacion-paises?cert=${encodeURIComponent(certId)}`,
  },
  "ampliacion-modelos": {
    nombre: "Ampliación / Reducción de Modelos o Productos",
    destino: (certId) => `/telecom/servicios/ampliacion-modelos?cert=${encodeURIComponent(certId)}`,
  },
  "ampliacion-fraccion": {
    nombre: "Ampliación / Reducción de Fracción Arancelaria",
    destino: (certId) => `/telecom/servicios/ampliacion-fraccion?cert=${encodeURIComponent(certId)}`,
  },
  "ampliacion-filiales": {
    nombre: "Ampliación / Reducción de Filiales, Subsidiarias y/o Importadores",
    destino: (certId) => `/telecom/servicios/ampliacion-filiales?cert=${encodeURIComponent(certId)}`,
  },
};

export function SeleccionCertificadoVigentePage() {
  const access = useTelecomAccess();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const s = params.get("s") ?? "";
  const servicio = SERVICIOS[s];
  const [busqueda, setBusqueda] = useState("");

  const q = useQuery<CertificadoVigente[]>({
    queryKey: ["telecom-certificados-vigentes"],
    queryFn: async () =>
      (await api.get<CertificadoVigente[]>("/api/sector/telecom/certificados/vigentes")).data,
    enabled: access.allowed,
  });

  const filas = useMemo(() => {
    const data = q.data ?? [];
    const t = busqueda.trim().toLowerCase();
    if (!t) return data;
    return data.filter(
      (c) =>
        c.numCertificado.toLowerCase().includes(t) ||
        c.titularRazonSocial.toLowerCase().includes(t),
    );
  }, [q.data, busqueda]);

  if (access.loading) {
    return (
      <div className="flex items-center justify-center p-12 text-sm text-fg-subtle">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Validando permisos…
      </div>
    );
  }
  if (!access.allowed) {
    return (
      <SectorNoHabilitadoPanel
        sectorNombre="Telecomunicaciones"
        sectorCodigo="TELECOM"
        titularRazonSocial={access.titularRazonSocial}
        sinContexto={access.sinContexto}
      />
    );
  }
  if (!servicio) {
    return (
      <Alert variant="warning">
        Servicio no reconocido. Vuelve al{" "}
        <Link to="/telecom/servicios" className="font-medium underline">menú de servicios</Link>.
      </Alert>
    );
  }

  const seleccionar = (certId: string) => navigate(servicio.destino(certId));

  return (
    <PageContainer width="wide">
      <PageHeader
        backTo="/telecom/servicios"
        backLabel="Cambiar servicio"
        title="Selección de Certificado"
        subtitle={
          <>
            Servicio solicitado: <span className="font-medium text-primary-strong">{servicio.nombre}</span>.
            Selecciona el certificado vigente sobre el cual generar la solicitud.
          </>
        }
      />

      <Card padding="none">
        <div className="flex items-center gap-2 border-b border-edge/60 p-4">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-fg-faint" />
            <Input
              type="text"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Buscar por número de certificado o razón social…"
              className="pl-9"
            />
          </div>
        </div>

        {q.isLoading && <div className="p-6 text-sm text-fg-subtle">Cargando certificados…</div>}
        {q.error && (
          <div className="p-4">
            <Alert variant="warning">No se pudieron cargar los certificados vigentes.</Alert>
          </div>
        )}
        {q.data && (
          <>
            <div className="border-b border-edge/60 px-4 py-2 text-xs uppercase tracking-wide text-fg-subtle">
              {filas.length} certificado{filas.length === 1 ? "" : "s"} vigente{filas.length === 1 ? "" : "s"}
            </div>
            {filas.length === 0 ? (
              <div className="p-6 text-sm text-fg-subtle">
                No hay certificados vigentes disponibles para tu contexto.
              </div>
            ) : (
              <>
              {/* Móvil: tarjetas apiladas (la tabla es demasiado ancha) */}
              <ul className="divide-y sm:hidden">
                {filas.map((c) => (
                  <li key={c.certificadoId} className="p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-mono text-xs font-medium text-fg">{c.numCertificado}</div>
                        <div className="mt-0.5 text-sm font-medium text-fg">{c.titularRazonSocial}</div>
                        <div className="font-mono text-[10px] text-fg-subtle">{c.solicitudFolio}</div>
                      </div>
                      <span className="shrink-0 rounded-full bg-estado-ok-bg px-2 py-0.5 text-xs font-medium text-estado-ok">
                        Vigente
                      </span>
                    </div>
                    <div className="mt-2 text-xs text-fg-muted">{`${c.productoNombre} ${c.productoModelo}`}</div>
                    <div className="mt-0.5 text-xs text-fg-subtle">Emisión: {formatDate(c.fechaEmision)}</div>
                    <Button
                      size="sm"
                      className="mt-3 w-full"
                      icon={<Check className="h-4 w-4" />}
                      onClick={() => seleccionar(c.certificadoId)}
                    >
                      Seleccionar
                    </Button>
                  </li>
                ))}
              </ul>

              {/* Escritorio: tabla */}
              <div className="hidden w-full overflow-x-auto sm:block">
              <table className="min-w-full text-sm">
                <thead className="bg-surface-2">
                  <tr>
                    {["Núm. de Certificado", "Titular", "Producto", "Emisión", "Estatus", ""].map((h) => (
                      <th key={h} className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-fg-subtle">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-edge bg-surface">
                  {filas.map((c) => (
                    <tr key={c.certificadoId} className="transition hover:bg-primary-tint">
                      <td className="px-4 py-3 align-top font-mono text-xs font-medium">{c.numCertificado}</td>
                      <td className="px-4 py-3 align-top">
                        <div className="font-medium text-fg">{c.titularRazonSocial}</div>
                        <div className="text-[10px] font-mono text-fg-subtle">{c.solicitudFolio}</div>
                      </td>
                      <td className="px-4 py-3 align-top text-xs">{`${c.productoNombre} ${c.productoModelo}`}</td>
                      <td className="px-4 py-3 align-top text-xs">{formatDate(c.fechaEmision)}</td>
                      <td className="px-4 py-3 align-top">
                        <span className="inline-block rounded-full bg-estado-ok-bg px-2 py-0.5 text-xs font-medium text-estado-ok">
                          Vigente
                        </span>
                      </td>
                      <td className="px-4 py-3 align-top text-right">
                        <Button
                          size="sm"
                          icon={<Check className="h-3 w-3" />}
                          onClick={() => seleccionar(c.certificadoId)}
                        >
                          Seleccionar
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
              </>
            )}
          </>
        )}
      </Card>

      {q.data && q.data.length === 0 && (
        <Alert variant="info">
          Para probar este trámite necesitas al menos un certificado emitido. Crea una solicitud
          de Certificado único, llévala hasta ACEPTADA y emítele su certificado desde "Por certificar".
        </Alert>
      )}
    </PageContainer>
  );
}

function formatDate(s: string): string {
  try {
    return new Date(s).toLocaleDateString("es-MX", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch {
    return s;
  }
}
